/**
 * Applicability Rule Evaluator (Issue #1480)
 *
 * Pure-function evaluator for JSON-Logic-style rules attached to
 * `LoanStageRequirement.applicabilityRule`. Used by the case-management gate
 * (`checkStageExit`) and by the business-portal `ClosingTab` to decide whether
 * a stage requirement currently applies given a case's context.
 *
 * Why JSON not free-form expressions:
 *  - safe (no `eval`, no parser dep)
 *  - zod-validatable on write
 *  - render-friendly (UI builds/displays the tree without parsing)
 *  - forward-compatible (new operators are additive)
 *
 * Failure mode: a malformed rule returns `true` (fail-open) and reports via
 * the optional `onMalformed` callback. Rationale — for a closing checklist,
 * fail-closed would silently hide a possibly-required item from the closer;
 * fail-open surfaces it as "currently applies" so it's visible and the closer
 * can override or complete it.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ApplicabilityLeafOp = 'eq' | 'in' | 'gt' | 'lt' | 'gte' | 'lte' | 'present';

export interface ApplicabilityLeafRule {
  field: string;
  op: ApplicabilityLeafOp;
  value?: unknown;
}

export type ApplicabilityRule =
  | ApplicabilityLeafRule
  | { all: ApplicabilityRule[] }
  | { any: ApplicabilityRule[] }
  | { not: ApplicabilityRule };

/**
 * Context evaluated against the rule. Known fields are typed for editor
 * autocompletion; unknown fields are accepted via the index signature so the
 * shape can grow without recompiling shared-types consumers.
 *
 * Known fields are the ones the seed in #1480 Part C exercises:
 *   - loanAmount, scopeAmount: dollar amounts (number)
 *   - borrowerTier: integer 1..9 (1 = best; relaxed from 1|2|3 in the
 *     1Sharpe warehouse-line follow-up to support WL conventions that
 *     ship more than three tiers)
 *   - loanPurpose: 'purchase' | 'refinance' | 'cash_out' | 'bridge'
 *   - backgroundReportFlagged: boolean
 *   - exitCount36mo: number of completed exits in trailing 36 months
 */
export interface ApplicabilityContext {
  loanAmount?: number;
  scopeAmount?: number;
  borrowerTier?: number;
  loanPurpose?: 'purchase' | 'refinance' | 'cash_out' | 'bridge';
  backgroundReportFlagged?: boolean;
  exitCount36mo?: number;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

// Per-operator leaf shape so `value` is required where the evaluator needs it
// (gt/lt/gte/lte require a number; in requires a non-empty array; eq accepts
// any defined value; present needs no value). Without these constraints a
// malformed leaf like `{ field: 'loanAmount', op: 'gt' }` would pass zod on
// the write path but evaluate to `false` at runtime — which would silently
// skip a REQUIRED requirement and let the gate allow stage exit. (PR #1482
// Copilot review.)
const leafRuleSchema = z.discriminatedUnion('op', [
  z
    .object({
      field: z.string().min(1),
      op: z.literal('present'),
    })
    .strict(),
  z
    .object({
      field: z.string().min(1),
      op: z.literal('eq'),
      // eq accepts any defined value (including booleans / strings / numbers);
      // null is rejected because evalLeaf treats null context as "not present"
      // and `value: null` would be ambiguous with the missing-context case.
      value: z.unknown().refine((v) => v !== undefined && v !== null, {
        message: 'eq value must be defined',
      }),
    })
    .strict(),
  z
    .object({
      field: z.string().min(1),
      op: z.literal('in'),
      value: z.array(z.unknown()).min(1),
    })
    .strict(),
  z
    .object({
      field: z.string().min(1),
      op: z.literal('gt'),
      value: z.number(),
    })
    .strict(),
  z
    .object({
      field: z.string().min(1),
      op: z.literal('lt'),
      value: z.number(),
    })
    .strict(),
  z
    .object({
      field: z.string().min(1),
      op: z.literal('gte'),
      value: z.number(),
    })
    .strict(),
  z
    .object({
      field: z.string().min(1),
      op: z.literal('lte'),
      value: z.number(),
    })
    .strict(),
]);

// `all` / `any` require at least one child so `{ any: [] }` (which evaluates
// to `false`) cannot be persisted. `not` requires exactly one child rule.
export const applicabilityRuleSchema: z.ZodType<ApplicabilityRule> = z.lazy(() =>
  z.union([
    leafRuleSchema,
    z.object({ all: z.array(applicabilityRuleSchema).min(1) }).strict(),
    z.object({ any: z.array(applicabilityRuleSchema).min(1) }).strict(),
    z.object({ not: applicabilityRuleSchema }).strict(),
  ]),
);

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

export interface EvaluateApplicabilityOptions {
  /**
   * Called when the rule fails zod validation. The evaluator returns `true`
   * (fail-open) regardless. Use this hook to surface a Loki/Sentry warning so
   * misconfigured rules are noticed in operations.
   */
  onMalformed?: (error: z.ZodError, rule: unknown) => void;
}

/**
 * Evaluate an applicability rule against a context. Returns `true` when the
 * rule says the requirement applies, `false` when it doesn't.
 *
 * Special cases:
 *  - `rule === null | undefined` → returns `true` (no rule = always applies).
 *    This matches the "NULL means always applies" convention on the Prisma
 *    column.
 *  - malformed rule → returns `true` and invokes `onMalformed` (fail-open).
 *  - leaf operator type mismatch (e.g. `gt` on a non-number) → returns `false`
 *    for that leaf. The whole-tree result depends on enclosing operators.
 */
export function evaluateApplicabilityRule(
  rule: unknown,
  context: ApplicabilityContext,
  options: EvaluateApplicabilityOptions = {},
): boolean {
  if (rule === null || rule === undefined) return true;

  const parsed = applicabilityRuleSchema.safeParse(rule);
  if (!parsed.success) {
    options.onMalformed?.(parsed.error, rule);
    return true;
  }

  return evalNode(parsed.data, context);
}

function evalNode(node: ApplicabilityRule, ctx: ApplicabilityContext): boolean {
  if ('all' in node) return node.all.every((child) => evalNode(child, ctx));
  if ('any' in node) return node.any.some((child) => evalNode(child, ctx));
  if ('not' in node) return !evalNode(node.not, ctx);
  return evalLeaf(node, ctx);
}

function evalLeaf(leaf: ApplicabilityLeafRule, ctx: ApplicabilityContext): boolean {
  const value = ctx[leaf.field];

  switch (leaf.op) {
    case 'present':
      return value !== undefined && value !== null;

    case 'eq':
      return value === leaf.value;

    case 'in':
      return Array.isArray(leaf.value) && leaf.value.includes(value as never);

    case 'gt':
      return typeof value === 'number' && typeof leaf.value === 'number' && value > leaf.value;

    case 'lt':
      return typeof value === 'number' && typeof leaf.value === 'number' && value < leaf.value;

    case 'gte':
      return typeof value === 'number' && typeof leaf.value === 'number' && value >= leaf.value;

    case 'lte':
      return typeof value === 'number' && typeof leaf.value === 'number' && value <= leaf.value;
  }
}
